import { AssetReference } from '@domain/creativeset/element-asset';
import { ElementKind } from '@domain/elements';
import { LibraryKind } from '@domain/media-library';
import { characterProperties } from '@domain/property';
import { VARIABLE_PREFIX } from '@domain/text';
import { cloneDeep } from '@studio/utils/clone';
import { uuidv4 } from '@studio/utils/id';
import { isElementVisibleAtTime } from '../animation.utils';
import { isVersionedText } from '../elements/rich-text/utils';
import { getFontStyleById, tryGetFontStyleById } from '../font-families.utils';
import { deserializeVersionedText } from '../serialization/text-serializer';
import { isHidden } from './data-node.utils';
import { isElementSelection } from './selection';
export function getKindAsString(kind) {
    switch (kind) {
        case LibraryKind.Text:
            return 'texts';
        case LibraryKind.Button:
            return 'buttons';
        case LibraryKind.Rectangle:
            return 'shapes';
        case LibraryKind.Ellipse:
            return 'shapes';
        case LibraryKind.Image:
            return 'images';
        case LibraryKind.Video:
            return 'videos';
        case LibraryKind.Widget:
            return 'widgets';
        case LibraryKind.Effects:
            return 'effects';
        case LibraryKind.Any:
            return 'elements';
        case LibraryKind.Feeds:
            return 'feeds';
        case LibraryKind.Selection:
            return 'selection';
    }
}
export function getKindTitleAsString(kind) {
    switch (kind) {
        case LibraryKind.Text:
            return 'Text Tool';
        case LibraryKind.Button:
            return 'Button Tool';
        case LibraryKind.Rectangle:
            return 'Rectangle Tool';
        case LibraryKind.Ellipse:
            return 'Ellipse Tool';
        case LibraryKind.Image:
            return 'Images';
        case LibraryKind.Video:
            return 'Videos';
        case LibraryKind.Widget:
            return 'Widgets';
        case LibraryKind.Effects:
            return 'Effects';
        case LibraryKind.Any:
            return 'Brand Library';
        case LibraryKind.Feeds:
            return 'Dynamic Content';
        case LibraryKind.Selection:
            return '';
    }
}
export function getKindMessageAsString(kind) {
    switch (kind) {
        case LibraryKind.Text:
            return 'Click/drag on canvas to\ncreate a new text area';
        case LibraryKind.Button:
            return 'Click/drag on canvas to\ncreate a new button';
        case LibraryKind.Rectangle:
            return 'Click/drag on canvas to\ncreate a new rectangle';
        case LibraryKind.Ellipse:
            return 'Click/drag on canvas to\ncreate a new ellipse';
        case LibraryKind.Image:
            return 'Drag in or upload an image\n to store it here';
        case LibraryKind.Video:
            return 'Drag in or upload an video\n to store it here';
        case LibraryKind.Widget:
            return 'Import a stock widget or\ncreate a new one from scratch';
        case LibraryKind.Effects:
            return 'Use a stock effect';
        case LibraryKind.Any:
            return 'Store elements that you\nwant to reuse across\ncreative sets in the library';
        case LibraryKind.Feeds:
            return 'Dynamic feed variables\nwill show up here';
        default:
            return '';
    }
}
const SEARCH_PLACEHOLDER_MAP = {
    [LibraryKind.Text]: 'Search text elements',
    [LibraryKind.Button]: 'Search buttons',
    [LibraryKind.Rectangle]: 'Search rectangles',
    [LibraryKind.Ellipse]: 'Search ellipses',
    [LibraryKind.Image]: 'Search images',
    [LibraryKind.Widget]: 'Search widgets',
    [LibraryKind.Effects]: 'Search effects',
    [LibraryKind.Video]: 'Search videos',
    [LibraryKind.Feeds]: 'Search dynamic content',
    library: 'Search in library'
};
export function getLibraryKindFromElementKind(kind) {
    switch (kind) {
        case ElementKind.Text:
            return LibraryKind.Text;
        case ElementKind.Button:
            return LibraryKind.Button;
        case ElementKind.Rectangle:
            return LibraryKind.Rectangle;
        case ElementKind.Ellipse:
            return LibraryKind.Ellipse;
        case ElementKind.Image:
            return LibraryKind.Image;
        case ElementKind.Video:
            return LibraryKind.Video;
        case ElementKind.Widget:
        case ElementKind.BannerflowLibraryWidget:
        case ElementKind.BannerflowLibraryWidgetInstance:
            return LibraryKind.Widget;
        default:
            return LibraryKind.Any;
    }
}
export function getElementKindFromLibraryKind(kind) {
    if (kind === LibraryKind.Any || kind === LibraryKind.Feeds || kind === LibraryKind.Selection) {
        return;
    }
    switch (kind) {
        case LibraryKind.Text:
            return ElementKind.Text;
        case LibraryKind.Button:
            return ElementKind.Button;
        case LibraryKind.Rectangle:
            return ElementKind.Rectangle;
        case LibraryKind.Ellipse:
            return ElementKind.Ellipse;
        case LibraryKind.Image:
            return ElementKind.Image;
        case LibraryKind.Video:
            return ElementKind.Video;
        case LibraryKind.Widget:
        case LibraryKind.Effects:
            return ElementKind.Widget;
        default:
            throw new Error(`Unknown library kind: ${kind}`);
    }
}
export function getSearchPlaceholderAsString(kind) {
    var _a;
    return (_a = SEARCH_PLACEHOLDER_MAP[kind !== null && kind !== void 0 ? kind : 'library']) !== null && _a !== void 0 ? _a : SEARCH_PLACEHOLDER_MAP.library;
}
export function mergeWidgetKind(kind) {
    if (isWidgetNode({ kind })) {
        return ElementKind.Widget;
    }
    return kind;
}
// Helper function to in order to make typing easier
function isNodeWithKind(element) {
    return Boolean(element && 'kind' in element);
}
function buildIsOfKind(...kinds) {
    return function (node) {
        if (!isNodeWithKind(node)) {
            return false;
        }
        return kinds.findIndex(testKind => node.kind === testKind) !== -1;
    };
}
export const isTextNode = buildIsOfKind(ElementKind.Button, ElementKind.Text);
export const isButtonNode = buildIsOfKind(ElementKind.Button);
export const isEllipseNode = buildIsOfKind(ElementKind.Ellipse);
export const isRectangleNode = buildIsOfKind(ElementKind.Rectangle);
export const isCreativeNode = buildIsOfKind("creative" /* CreativeKind.Creative */);
export const isVideoNode = buildIsOfKind(ElementKind.Video);
export const isImageNode = buildIsOfKind(ElementKind.Image);
export const isImageOrVideoNode = buildIsOfKind(ElementKind.Image, ElementKind.Video);
export function isGroupDataNode(element) {
    return !!element && buildIsOfKind(ElementKind.Group)(element);
}
export function isGroupPrimitiveDataNode(element) {
    return !!element && buildIsOfKind(ElementKind.Group)(element);
}
export const isWidgetNode = buildIsOfKind(ElementKind.Widget, ElementKind.BannerflowLibraryWidget, ElementKind.BannerflowLibraryWidgetInstance);
function isNodeDtoKind(node, kind) {
    return Boolean(node[`__${kind}Kind`]);
}
export function isRectangleNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Rectangle);
}
export function isEllipseNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Ellipse);
}
export function isTextNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Text);
}
export function isButtonNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Button);
}
export function isVideoNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Video);
}
export function isImageNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Image);
}
export function isWidgetNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Widget);
}
export function isGroupNodeDto(node) {
    return isNodeDtoKind(node, ElementKind.Group);
}
export function isWidgetElementDto(element) {
    return (element !== undefined &&
        (element.type === ElementKind.Widget ||
            element.type === ElementKind.BannerflowLibraryWidget ||
            element.type === ElementKind.BannerflowLibraryWidgetInstance));
}
export function isTextLikeElement(element) {
    return element !== undefined && [ElementKind.Text, ElementKind.Button].includes(element.type);
}
export function isVideoElement(element) {
    return element !== undefined && element.type === ElementKind.Video;
}
export function isImageElement(element) {
    return element !== undefined && element.type === ElementKind.Image;
}
export function isImageOrVideoElement(element) {
    return element !== undefined && (isImageElement(element) || isVideoElement(element));
}
export function isTextDataElement(node) {
    return (node !== undefined &&
        (node.kind === ElementKind.Button || node.kind === ElementKind.Text) &&
        !isViewElementNode(node));
}
export function isWidgetElement(element) {
    return (element !== undefined &&
        [
            ElementKind.Widget,
            ElementKind.BannerflowLibraryWidget,
            ElementKind.BannerflowLibraryWidgetInstance
        ].includes(element.type));
}
export function isBrandLibraryWidgetElement(element) {
    return (element !== undefined &&
        [ElementKind.Widget, ElementKind.BannerflowLibraryWidget].includes(element.type));
}
export function isWidgetElementWithTranslatableContent(element) {
    return (isWidgetElement(element) &&
        Boolean(element.properties.find(property => property.unit === 'text' || property.unit === 'feed')));
}
export function getDynamicPropertyOfElement(element) {
    // Exclude widget, since "dynamicContent" on widget is a feed custom property
    // and not a dynamic property for SDA.
    if (!isWidgetElement(element)) {
        return element === null || element === void 0 ? void 0 : element.properties.find(prop => prop.name === 'dynamicContent');
    }
}
export function isViewElementNode(node) {
    if (node === undefined || !('kind' in node)) {
        return false;
    }
    return node.kind !== "creative" /* CreativeKind.Creative */ && node.kind !== ElementKind.Group && node['__data'];
}
export function isSelectionVisibleAtTime(element, time) {
    return !isHidden(element) && isVisibleAtTime(element, time);
}
export function isVisibleAtTime(element, time) {
    return time >= element.time && time <= element.time + element.duration;
}
export function isState(element) {
    if (!element) {
        return false;
    }
    return !('kind' in element) || typeof element.kind !== 'string';
}
export function isElementDataNode(element) {
    return !isState(element) && !isViewElementNode(element);
}
export function canBeMask(node) {
    return isRectangleNode(node) || isEllipseNode(node) || isImageNode(node);
}
export function isMaskingSupported(node) {
    return canBeMask(node) || isVideoNode(node);
}
export function isMaskingSupportedDto(node) {
    return (isRectangleNodeDto(node) ||
        isEllipseNodeDto(node) ||
        isImageNodeDto(node) ||
        isVideoNodeDto(node));
}
export function isUsedInMask(node) {
    return isMaskingSupported(node) && node.masking !== undefined;
}
export function isMaskNode(node) {
    return isUsedInMask(node) && node.masking.isMask === true;
}
export function isMaskedNode(node) {
    return isUsedInMask(node) && node.masking.elementId !== undefined;
}
export function getClosestMaskIndex(nodeIndex, nodes) {
    for (let i = nodeIndex; i < nodes.length; i++) {
        const node = nodes[i];
        if (isMaskNode(node)) {
            return i;
        }
    }
    return -1;
}
export function isDynamicCreativeElement(element) {
    if (isWidgetNode(element)) {
        return element.customProperties.some(prop => prop.dynamicPropertyId);
    }
    return (isImageNode(element) || isTextNode(element)) && !!element.dynamicPropertyId;
}
export function getClosestGroupIndex(nodeIndex, nodes) {
    for (let i = nodeIndex; i < nodes.length; i++) {
        const node = nodes[i];
        if (isGroupDataNode(node)) {
            return i;
        }
    }
    return -1;
}
export function sortToMaskable(nodes) {
    const newArr = [];
    for (const node of nodes) {
        const hasMaskableNode = newArr.some(canBeMask);
        if (!hasMaskableNode && canBeMask(node)) {
            newArr.unshift(node);
        }
        else {
            newArr.push(node);
        }
    }
    return newArr;
}
export function createDefaultTextProperties(dataOverride) {
    var _a, _b, _c, _d, _e, _f;
    /** TODO: Fix these typecasts at some point for safer typing */
    const properties = {
        padding: (_a = dataOverride === null || dataOverride === void 0 ? void 0 : dataOverride.padding) !== null && _a !== void 0 ? _a : { top: 0, left: 0, right: 0, bottom: 0 },
        verticalAlignment: (_b = dataOverride === null || dataOverride === void 0 ? void 0 : dataOverride.verticalAlignment) !== null && _b !== void 0 ? _b : 'top',
        maxRows: (_c = dataOverride === null || dataOverride === void 0 ? void 0 : dataOverride.maxRows) !== null && _c !== void 0 ? _c : Infinity,
        textOverflow: (_d = dataOverride === null || dataOverride === void 0 ? void 0 : dataOverride.textOverflow) !== null && _d !== void 0 ? _d : 'expand',
        characterSpacing: (_e = dataOverride === null || dataOverride === void 0 ? void 0 : dataOverride.characterSpacing) !== null && _e !== void 0 ? _e : 0,
        lineHeight: (_f = dataOverride === null || dataOverride === void 0 ? void 0 : dataOverride.lineHeight) !== null && _f !== void 0 ? _f : 1.3,
        __fontFamilyId: ''
    };
    return properties;
}
const characters = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z'
];
let currentCidIndex = 1;
export function createElementCid() {
    const position = currentCidIndex++;
    return getNumberSystemEncoding(position);
}
export function getNumberSystemEncoding(position) {
    const numberSystem = characters.length;
    if (position <= numberSystem) {
        return characters[position - 1];
    }
    const numerals = [];
    let currentNumber = position;
    while (currentNumber >= numberSystem) {
        const remainder = currentNumber % numberSystem;
        currentNumber = (currentNumber - remainder) / numberSystem;
        numerals.push(characters[remainder]);
    }
    numerals.push(characters[currentNumber - 1]);
    return numerals.reverse().join('');
}
export function resetNodeCid() {
    currentCidIndex = 1;
}
export function getCurrentCidIndex() {
    return currentCidIndex;
}
export function forEachElement(group, callback, atTime, reverse) {
    forEachNode(group, callback, atTime, reverse);
}
export function forEachDataElement(group, callback, atTime, reverse) {
    forEachNode(group, callback, atTime, reverse);
}
function forEachNode(group, callback, atTime, reverse) {
    let nodes = [];
    if (isNodeWithKind(group)) {
        switch (group.kind) {
            case "creative" /* CreativeKind.Creative */:
            case ElementKind.Group:
                nodes = group.nodes;
                break;
            default:
                throw new Error('Unknown group element.');
        }
    }
    else {
        nodes = group;
    }
    if (reverse) {
        nodes = nodes.slice().reverse();
    }
    let i = 0;
    for (const node of nodes) {
        if (isGroupDataNode(node)) {
            forEachNode(node, groupNode => {
                callback(groupNode, node, i);
            }, atTime, reverse);
        }
        else if (typeof atTime !== 'number' ||
            isElementVisibleAtTime(node, atTime)) {
            callback(node, undefined, i);
        }
        i++;
    }
}
/**
 * @returns Returns a flat nodelist of element nodes. Groups are excluded.
 */
export function toFlatElementNodeList(nodes) {
    const flatNodeList = [];
    forEachDataElement(nodes, element => {
        if (isGroupDataNode(element)) {
            return;
        }
        flatNodeList.push(element);
    });
    return flatNodeList;
}
/**
 * @returns Returns a completely flattened array of all nodes, including groups.
 */
export function toFlatNodeList(nodes) {
    if (Array.isArray(nodes) && nodes.every(node => !isGroupDataNode(node))) {
        return nodes;
    }
    const flatNodeListArr = [];
    const nodeArr = 'nodes' in nodes ? nodes.nodes : nodes;
    for (const node of nodeArr.slice().reverse()) {
        if (isGroupDataNode(node)) {
            flatNodeListArr.unshift(node);
            flatNodeListArr.unshift(...toFlatNodeList(node));
        }
        else if (flatNodeListArr.indexOf(node) === -1) {
            flatNodeListArr.unshift(node);
        }
    }
    return flatNodeListArr;
}
/**
 * @param  {OneOfDataNodes|undefined} node
 * @param  {(parent:IGroupElementDataNode)=>boolean|void} callback
 * Return true to break loop
 */
export function forEachParentNode(node, callback) {
    if (!node) {
        return;
    }
    let parentNode = node.__parentNode;
    while (parentNode) {
        const cancel = callback(parentNode);
        if (cancel) {
            return;
        }
        parentNode = parentNode.__parentNode;
    }
}
export function positionIsInBounds(position, bounds, tolerance = { x: 0, y: 0 }) {
    let checkLeftBound;
    let checkTopBound;
    let checkRightBound;
    let checkBottomBound;
    if ('scaleX' in bounds && bounds.scaleX) {
        checkLeftBound = position.x >= bounds.x + bounds.width / 2 - (bounds.width * bounds.scaleX) / 2;
        checkRightBound =
            position.x <= bounds.x + bounds.width / 2 + (bounds.width * bounds.scaleX) / 2;
    }
    else {
        const minX = bounds.x - tolerance.x;
        const maxX = bounds.x + bounds.width + tolerance.x;
        checkLeftBound = position.x >= minX;
        checkRightBound = position.x <= maxX;
    }
    if ('scaleY' in bounds && bounds.scaleY) {
        checkTopBound =
            position.y >= bounds.y + bounds.height / 2 - (bounds.height * bounds.scaleY) / 2;
        checkBottomBound =
            position.y <= bounds.y + bounds.height / 2 + (bounds.height * bounds.scaleY) / 2;
    }
    else {
        const minY = bounds.y - tolerance.y;
        const maxY = bounds.y + bounds.height + tolerance.y;
        checkTopBound = position.y >= minY;
        checkBottomBound = position.y <= maxY;
    }
    if (checkLeftBound && checkRightBound) {
        if (checkTopBound && checkBottomBound) {
            return true;
        }
    }
    return false;
}
export function isNumberBetween(origin, num2, margin = 5) {
    return (origin - num2 - margin) * (origin - num2 + margin) <= 0;
}
export function getElementTextProperties(data) {
    const style = {};
    for (const characterProperty of characterProperties) {
        const value = data[characterProperty];
        if (value !== undefined) {
            style[characterProperty] = value;
        }
    }
    return style;
}
export function getElementIndex(element) {
    return element.__rootNode.elements.findIndex(e => e.id === element.id);
}
export function asSortedArray(elements) {
    return Array.from(elements).sort((a, b) => getElementIndex(a) - getElementIndex(b));
}
export function getNodeIndex(element) {
    return toFlatNodeList(element.__rootNode.nodes).findIndex(e => e.id === element.id);
}
export function getElementIdentifier(elementOrSelection) {
    if (isElementSelection(elementOrSelection)) {
        if (elementOrSelection.length === 1) {
            const element = elementOrSelection.element;
            return `${element.kind}:${element.id}`;
        }
        else {
            return `Selection: ${elementOrSelection.length}`;
        }
    }
    else {
        const element = elementOrSelection;
        return `${element.kind}:${element.id}`;
    }
}
export function createViewElement(element) {
    if (isGroupDataNode(element)) {
        throw new Error('View elements can not be created from a group node.');
    }
    const viewElement = {
        kind: element.kind,
        __data: element
    };
    viewElement.elementCid = createElementCid();
    viewElement.cidIndex = getCurrentCidIndex();
    viewElement.id = element.id;
    viewElement.name = element.name;
    viewElement.ratio = element.ratio;
    viewElement.time = element.time;
    viewElement.duration = element.duration;
    if (isTextNode(viewElement) && isTextNode(element)) {
        viewElement.font = element.font;
        viewElement.content = element.content;
    }
    else if (isImageNode(element) && isImageNode(viewElement)) {
        viewElement.feed = element.feed;
    }
    else if (isWidgetNode(viewElement) && isWidgetNode(element)) {
        viewElement.customProperties = element.customProperties;
    }
    return viewElement;
}
export function initializeElements(creative, values) {
    var _a, _b, _c;
    for (const node of creative.nodeIterator_m(true)) {
        if (!node.globalElement) {
            console.error(`Could not find global element with id '${node.id}'.`);
            if (isWidgetNode(node)) {
                // AD-2457: Don't attempt to migrate widget nodes
                continue;
            }
            const properties = [];
            if (isImageNode(node)) {
                properties.push({
                    id: '',
                    name: AssetReference.Image,
                    unit: 'id',
                    value: (_a = node.imageAsset) === null || _a === void 0 ? void 0 : _a.id,
                    clientId: uuidv4()
                });
            }
            if (isVideoNode(node)) {
                properties.push({
                    id: '',
                    name: AssetReference.Video,
                    unit: 'id',
                    value: (_b = node.videoAsset) === null || _b === void 0 ? void 0 : _b.id,
                    clientId: uuidv4()
                });
            }
            node.globalElement = {
                id: node.id,
                name: (_c = node.name) !== null && _c !== void 0 ? _c : node.kind,
                type: node.kind,
                properties
            };
            // eslint-disable-next-line no-console
            console.debug(`Soft migrated element with id ${node.id}`);
        }
        if (isGroupDataNode(node)) {
            continue;
        }
        initializeElementProperties(node, {
            elementProperties: node.globalElement.properties,
            versionProperties: values.versionProperties,
            defaultVersionProperties: values.defaultVersionProperties,
            fontFamilies: values.fontFamilies
        });
    }
}
export function initializeFonts(textElement, fontFamilies) {
    for (const style of textElement.characterStyles.values()) {
        if (style.font) {
            if (typeof style.font === 'string') {
                try {
                    const fontStyle = getFontStyleById(fontFamilies, style.font);
                    style.font = {
                        id: fontStyle.id,
                        src: fontStyle.fontUrl,
                        weight: fontStyle.weight,
                        style: fontStyle.italic ? 'italic' : 'normal',
                        fontFamilyId: fontStyle.fontFamilyId
                    };
                }
                catch (err) {
                    console.error(err);
                    delete style.font;
                }
            }
        }
    }
}
export function initializeElementProperties(element, values) {
    var _a;
    const { elementProperties, versionProperties: versionValues, defaultVersionProperties: defaultVersionValues } = values;
    if (isTextNode(element)) {
        const fontStyleId = ((_a = element.font) === null || _a === void 0 ? void 0 : _a.id) || element.__fontStyleId;
        const fontStyle = tryGetFontStyleById(values.fontFamilies, fontStyleId || '');
        element.font = undefined;
        if (fontStyle) {
            // Note: font is undefined before initialization, we just store the font style id as a reference.
            element.font = {
                id: fontStyle.id,
                src: fontStyle.fontUrl,
                weight: fontStyle.weight,
                style: fontStyle.italic ? 'italic' : 'normal',
                fontFamilyId: fontStyle.fontFamilyId
            };
        }
    }
    for (const property of elementProperties) {
        if (property.versionPropertyId) {
            let versionValue = versionValues.find(v => v.id === property.versionPropertyId);
            if (!versionValue) {
                versionValue = defaultVersionValues.find(e => e.id === property.versionPropertyId);
            }
            if (!versionValue) {
                throw new Error(`Could not find value from global elements, selected version or original. ID: ${property.versionPropertyId}`);
            }
            const name = property.name;
            if (name === 'content' && isTextNode(element) && isVersionedText(versionValue)) {
                initializeFonts(element, values.fontFamilies);
                element.content = createTextFromVersionedText(element, versionValue.value, element.characterStyles);
            }
            else if (isWidgetNode(element)) {
                const versionProperty = element.customProperties.find(prop => prop.versionPropertyId === property.versionPropertyId);
                if (versionProperty) {
                    versionProperty.value = versionValue.value;
                }
            }
            else {
                element[name] = versionValue.value;
            }
        }
        else {
            element[property.name] = property.value;
        }
    }
}
export function createVersionedTextFromText(text) {
    let position = 0;
    const textSpans = [];
    let _text = '';
    for (const span of text.spans) {
        if (span.type === "end" /* SpanType.End */) {
            break;
        }
        const length = span.type === "variable" /* SpanType.Variable */
            ? span.style.variable.path.length + 1
            : span.content.length;
        textSpans.push({
            position,
            length,
            type: span.type,
            styleIds: Object.assign({}, (span.styleIds || {})),
            variable: span.style.variable,
            __previousStyleIds: [...(span.__previousStyleIds || [])],
            __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
        });
        position += length;
        _text +=
            span.type === "variable" /* SpanType.Variable */
                ? VARIABLE_PREFIX + span.style.variable.path
                : span.content;
    }
    return {
        text: _text,
        styles: textSpans
    };
}
export function createInlineStyledTextFromText(text) {
    let position = 0;
    const textSpans = [];
    let _text = '';
    const copyText = JSON.parse(JSON.stringify(text));
    for (const span of copyText.spans) {
        if (span.type === "end" /* SpanType.End */) {
            break;
        }
        const length = span.content.length;
        textSpans.push({
            position,
            length,
            type: span.type,
            style: span.style,
            variable: span.style.variable
        });
        position += length;
        _text += span.content;
    }
    return {
        text: _text,
        styles: textSpans
    };
}
export function createTextFromInlineStyledText(inlineStyledText, textProperties) {
    const text = inlineStyledText.text;
    const spans = [];
    for (const span of inlineStyledText.styles) {
        switch (span.type) {
            case "word" /* SpanType.Word */:
            case "variable" /* SpanType.Variable */:
            case "space" /* SpanType.Space */: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: text.substr(span.position, span.length),
                    style: cloneDeep(span.style),
                    styleIds: {},
                    __previousStyleIds: [],
                    __previousStyleIdToHistoryIndexMap: new Map()
                });
                break;
            }
            case "newline" /* SpanType.Newline */: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: '\n',
                    style: cloneDeep(span.style),
                    styleIds: {},
                    __previousStyleIds: [],
                    __previousStyleIdToHistoryIndexMap: new Map()
                });
                break;
            }
            default:
                throw new TypeError(`Expected span of type 'word', 'space' 'newline', instead got '${span.type}'.`);
        }
    }
    spans.push({
        type: "end" /* SpanType.End */,
        content: 'END'
    });
    return {
        style: textProperties !== null && textProperties !== void 0 ? textProperties : createDefaultTextProperties(),
        spans
    };
}
export function createTextFromVersionedText(element, versionedText, elementsCharacterStyles) {
    // This should hopefully not be a problem since the types have been updated, but I'll leave it here
    // just in case
    if (!element.__rootNode) {
        throw new Error(`Expected versioned text with root node, received '${typeof element.__rootNode}'`);
    }
    if (typeof versionedText === 'string') {
        versionedText = deserializeVersionedText(versionedText);
    }
    const text = versionedText.text;
    const spans = [];
    for (const span of versionedText.styles) {
        const styleId = span.styleIds[element.__rootNode.id];
        const characterStyleValue = elementsCharacterStyles.get(styleId) || {};
        switch (span.type) {
            case "variable" /* SpanType.Variable */: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: VARIABLE_PREFIX + span.variable.path,
                    style: Object.assign(cloneDeep(characterStyleValue), {
                        variable: span.variable
                    }),
                    styleIds: cloneDeep(span.styleIds),
                    styleId,
                    __previousStyleIds: [...(span.__previousStyleIds || [])],
                    __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
                });
                break;
            }
            case "word" /* SpanType.Word */:
            case "space" /* SpanType.Space */: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: text.substr(span.position, span.length),
                    style: cloneDeep(characterStyleValue),
                    styleIds: cloneDeep(span.styleIds),
                    styleId,
                    __previousStyleIds: [...(span.__previousStyleIds || [])],
                    __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
                });
                break;
            }
            case "newline" /* SpanType.Newline */: {
                spans.push({
                    type: span.type,
                    attributes: {},
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    content: '\n',
                    style: cloneDeep(characterStyleValue),
                    styleIds: cloneDeep(span.styleIds),
                    styleId,
                    __previousStyleIds: [...(span.__previousStyleIds || [])],
                    __previousStyleIdToHistoryIndexMap: new Map(span.__previousStyleIdToHistoryIndexMap || [])
                });
                break;
            }
            default:
                throw new TypeError(`Expected span of type 'word', 'space' 'newline', instead got '${span.type}'.`);
        }
    }
    spans.push({
        type: "end" /* SpanType.End */,
        content: 'END'
    });
    return {
        style: createDefaultTextProperties(element),
        spans
    };
}
/**
 * If it is a widget imported from BF library or if the widget is custom but was published to the BF library
 */
export function isAnyBannerflowLibraryWidget(element) {
    return isBannerFlowLibraryWidget(element) || isOriginalBannerFlowLibraryWidget(element);
}
export function isBannerFlowLibraryWidget(element) {
    var _a;
    // If the type is of bannerflowLibraryWidget then it's imported
    return (element.type === ElementKind.BannerflowLibraryWidget ||
        !!((_a = element.properties) === null || _a === void 0 ? void 0 : _a.find(prop => prop.name === AssetReference.BannerflowLibraryWidget)));
}
export function isCustomWidgetElement(element) {
    return element !== undefined && element.type === ElementKind.Widget;
}
export function isOriginalBannerFlowLibraryWidget(element) {
    var _a;
    // If the type is of bannerflowLibraryWidget then it's imported
    return !!(isCustomWidgetElement(element) &&
        ((_a = element.properties) === null || _a === void 0 ? void 0 : _a.find(prop => prop.name === AssetReference.BannerflowLibraryWidget)));
}
export function getTextElements(creative) {
    const result = [];
    forEachDataElement(creative, element => {
        if (isTextNode(element)) {
            result.push(element);
        }
    });
    return result;
}
export function isOrphanElement(element, creativeset) {
    for (const design of creativeset.designs) {
        const hasElement = design.elements.some(el => el.id === element.id);
        if (hasElement) {
            return false;
        }
    }
    return true;
}
export function ratioLockSvgElement(url, imageNode) {
    const isSvg = /(\.(svg))(\/?(\?.*)?)$/gi;
    if ((url || '').match(isSvg)) {
        imageNode.ratio = imageNode.width / imageNode.height;
    }
}
